import { nbVal } from "@paroi/data-formatters-lib";
import { useWrapAsync } from "@paroicms/front-app-log";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import type { SelectItem } from "primereact/selectitem";
import type { Nullable } from "primereact/ts-helpers";
import { useEffect, useState } from "react";
import { fetchRoles } from "../../../../../api-requests/roles";
import type { CreateUserInput, EditUserInput } from "../../../../../api-requests/users";
import { ClientError, apiRequestWrapper } from "../../../../../api/shared/http-client";
import { appLog } from "../../../../../context";
import type { Role } from "../../../../../helpers/format-roles";
import type { User } from "../../../../../helpers/format-users";
import OurDropdown from "../../../utils/OurDropdown/OurDropdown";
import Spinner from "../../../utils/Spinner/Spinner";

import { showToast } from "../../../../shared/global-tools/toast-system";
import "./UserForm.scss";

export function UserForm({
  action,
  successMessage,
  errorMessage,
  userToEdit,
  refreshUsers,
  save,
  onSave,
}: {
  action: "create" | "edit";
  successMessage: string;
  errorMessage: string;
  userToEdit?: User;
  refreshUsers(): void;
  save(body: CreateUserInput | EditUserInput): Promise<void>;
  onSave?(): void;
}) {
  const required = action === "create";
  const wrapAsync = useWrapAsync();
  const [loading, setLoading] = useState(false);
  const [lastname, setLastname] = useState<string>("");
  const [firstname, setFirstName] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<Nullable<string>>();
  const [email, setEmail] = useState<string>("");
  const [selectedRole, setSelectedRole] = useState<number>();
  const [roles, setRoles] = useState<Role[]>();

  const handleUserCreation = wrapAsync(async () => {
    if (selectedRole === undefined || !phoneNumber) return;
    const result = phoneNumber.match(/^(\d{10})$/);
    if (!result) {
      showToast("Veuillez entrer un numéro de téléphone valide", { severity: "warn" });
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showToast("Veuillez entrer une adresse email valide", { severity: "warn" });
      return;
    }
    setLoading(true);

    try {
      await save({
        firstname,
        lastname,
        email,
        phone: `229${phoneNumber}`,
        roleId: selectedRole,
      });
      refreshUsers();
      onSave?.();
      showToast(successMessage, { severity: "success" });
    } catch (error) {
      appLog.error(error);
      if (error instanceof ClientError) {
        const message = error.fieldErrors
          ? Object.entries(error.fieldErrors)
              .flatMap(([attribute, msgs]) => msgs.map((msg) => `${attribute}: ${msg}`))
              .join("\n")
          : error.message;
        showToast(message, {
          severity: "error",
        });
      } else {
        showToast(errorMessage, { severity: "error" });
      }
    }
    setLoading(false);
  });

  useEffect(
    wrapAsync(async () => {
      await apiRequestWrapper(async () => {
        const fetchedRoles = await fetchRoles();
        setRoles(fetchedRoles);
      });
    }),
    [],
  );
  useEffect(() => {
    if (!roles) return;
    if (userToEdit) {
      setFirstName(userToEdit.firstname);
      setLastname(userToEdit.lastname);
      setEmail(userToEdit.email);
      setPhoneNumber(userToEdit.phone ? userToEdit.phone.replace(/^229/, "") : undefined);

      if (userToEdit.roles[0]) setSelectedRole(userToEdit.roles[0].id);
      else setSelectedRole(roles[0]?.id);
    } else setSelectedRole(roles[0]?.id);
  }, [userToEdit, roles]);

  if (!roles) return <Spinner />;

  const dropdownOptions: SelectItem[] = roles.map((r) => ({
    label: r.name,
    value: r.id,
  }));

  return (
    <form
      onSubmit={(ev) => {
        ev.preventDefault();
        handleUserCreation();
      }}
      className="Card UserForm"
    >
      <div className="UserForm-body">
        <label className="Field">
          <span className="Field-label">
            Nom <span style={{ color: "red", fontWeight: "bold" }}>*</span>
          </span>
          <InputText
            className="Field-input w-full"
            required
            value={lastname}
            onChange={(ev) => setLastname(ev.target.value)}
          />
        </label>

        <label className="Field">
          <span className="Field-label">
            Prénom <span style={{ color: "red", fontWeight: "bold" }}>*</span>
          </span>
          <InputText
            className="Field-input w-full"
            value={firstname}
            onChange={(ev) => setFirstName(ev.target.value)}
            required
          />
        </label>

        <label className="Field">
          <span className="Field-label">
            Numéro de téléphone <span style={{ color: "red", fontWeight: "bold" }}>*</span>
          </span>
          <InputText
            type="tel"
            placeholder="0166001223"
            className="Field-input w-full"
            value={phoneNumber}
            onChange={(ev) => {
              setPhoneNumber(ev.target.value);
            }}
            required
            maxLength={10}
            keyfilter="num"
          />
        </label>

        <label className="Field">
          <span className="Field-label">
            Email {required && <span style={{ color: "red", fontWeight: "bold" }}>*</span>}
          </span>
          <InputText
            type="email"
            className="Field-input w-full"
            value={email}
            onChange={(ev) => setEmail(ev.target.value)}
            required={required}
          />
        </label>

        <label className="Field">
          <span className="Field-label">Role</span>
          <OurDropdown<number>
            selectedOption={selectedRole}
            initialOptions={dropdownOptions}
            onChange={(e) => setSelectedRole(nbVal(e))}
          />
        </label>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "32px",
        }}
      >
        <Button type="submit" className="Btn lg primary" label="Valider" loading={loading} />
      </div>
    </form>
  );
}
